import React from 'react';
import { ControlledTextArea } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-textarea';
import { ControlledFileUpload } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-file-upload';
import { Callout, FileWithUrl, FlexContainer, Icon, Link } from '@placer-ui/components';
import { MAX_ADDITIONAL_INFORMATION_LENGTH } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/advance-request-property/consts';
import { FormControl } from 'features/my-zone/support-requests/components/support-requests-modals/types';
import { SupportEmailInput } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import { getFeatureFlags } from 'core/flow-control';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';

import styles from './polygon-request-property.module.scss';

const SUPPORTED_FILE_TYPES = [
    'application/zip',
    'application/x-zip',
    'application/x-zip-compressed',
    '.kmz', // KMZ (compressed KML)
    '.kml', // KML (Google Earth format)
    'application/geo+json',
    '.geojson',
];

export interface PolygonFormValues {
    type: 'advance_property_polygon';
    additionalInformation: string;
    files?: FileWithUrl[];
    executiveEmail?: string;
    opportunityUrl?: string;
}

export const PolygonRequestProperty = ({
    formControl,
}: {
    formControl: FormControl<PolygonFormValues>;
}) => {
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();
    const { control } = formControl;

    return (
        <div className={styles.formContainer} data-testid="advance-polygon-request">
            {enable_request_properties_sales_flow_ff && (
                <FlexContainer gap={16}>
                    <SupportSalesEmailInput />
                    <SupportOpportunityUrlInput />
                </FlexContainer>
            )}
            {!enable_request_properties_sales_flow_ff && <SupportEmailInput />}
            <ControlledTextArea
                name="additionalInformation"
                control={control}
                initialHeight={100}
                label="Information"
                required
                asterix
                data-testid="advance-polygon-additional-information-input"
                feedback={[
                    {
                        message: `Up to ${MAX_ADDITIONAL_INFORMATION_LENGTH} characters`,
                        state: 'guidance',
                        hideIcon: true,
                    },
                ]}
                maxLength={MAX_ADDITIONAL_INFORMATION_LENGTH}
                placeholder="Share additional details to help our support team assist you better."
            />
            <ControlledFileUpload
                name="files"
                testId="advance-polygon-file-upload"
                label="Attachments"
                formats={SUPPORTED_FILE_TYPES}
                description="ZIP (SHP file), KMZ, KML, GeoJSON"
                feedback={[]}
                control={control}
                required
                asterix
            />
            <Callout
                FreeTextIcon={<Icon type="Info" />}
                text="A KML or SHP zip file is needed for POIs without official map boundaries. View how to create a KML in"
                CTA={
                    <>
                        <Link
                            target="_blank"
                            href="https://view-su2.highspot.com/viewer/665ed3e5b6fab709cabf5542"
                        >
                            Google Maps
                        </Link>
                        &nbsp; or &nbsp;
                        <Link
                            target="_blank"
                            href="https://view-su2.highspot.com/viewer/665ed47d96e761295ee17d8c"
                        >
                            Google Earth
                        </Link>
                    </>
                }
            />
        </div>
    );
};
